import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getLeagueCupStatus } from "core-integration/src/store/leagues/reducers";
import { fetchLeagueCupStatus } from "core-integration/src/store/leagues/thunks";
import MoreLink from "plos/src/components/links/MoreLink";
import PageTitle from "plos/src/components/PageTitle";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { rightSidebarLayout } from "plos/src/layouts";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { HelmetHead } from "../HelmetHead";
import { standingsMainContent } from "./Classic/StandingsClassic/standingsClassic.css";

const CupNotStarted = () => {
  const { t } = useTranslation("leaguesAndCups");
  const { leagueId } = useParams();
  const reduxDispatch = useDispatch<ThunkDispatch>();

  const leagueNumber = Number(leagueId!) || 0;

  const cupStatus = useSelector((state: RootState) =>
    leagueNumber ? getLeagueCupStatus(state, leagueNumber) : null,
  );

  useEffect(() => {
    if (leagueNumber && !cupStatus) {
      reduxDispatch(fetchLeagueCupStatus(leagueNumber));
    }
  }, [leagueNumber, cupStatus, reduxDispatch]);

  if (!cupStatus) {
    return null;
  }

  return (
    <>
      <HelmetHead
        title={t(
          "cupNotStarted.metadata.title",
          "{{cupStatusName}} - Pre Cup Match Info | Fantasy Premier League",
          {
            cupStatusName: cupStatus.name,
          },
        )}
        description={t(
          "cupNotStarted.metadata.description",
          "To view the {{cupStatusName}} pre cup match info, as well as creating & joining new leagues, visit the official website of the Premier League.",
          {
            cupStatusName: cupStatus.name,
          },
        )}
      />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={standingsMainContent}>
            <PageTitle title={cupStatus.name} />
            <MoreLink
              to={`/leagues/${leagueNumber}/standings/c`}
              variant="light"
            >
              {t("viewLeagueStandings", "View league standings")}
            </MoreLink>

            {cupStatus.qualification_event ? (
              <>
                <Subheading>
                  {t(
                    "cupNotStarted.cupScheduled",
                    "The Cup is scheduled to start in GW{{qualificationEvent}}",
                    { qualificationEvent: cupStatus.qualification_event + 1 },
                  )}
                </Subheading>

                {cupStatus.has_byes ? (
                  <>
                    <p>
                      {t(
                        "cups.fixturesDetermination",
                        "Fixtures will be determined at the end of Gameweek {{week}}.",
                        { week: cupStatus.qualification_event },
                      )}{" "}
                      {cupStatus.qualification_numbers > 2 &&
                        (cupStatus.qualification_numbers === 4 ? (
                          <Trans
                            ns="leaguesAndCups"
                            i18nKey="cups.qualificationInformationExact"
                            values={{
                              cupStatusQualificationNumbers:
                                cupStatus.qualification_numbers,
                              nextQualifyingEvent:
                                cupStatus.qualification_event + 1,
                              qualifyingEvent: cupStatus.qualification_event,
                            }}
                          >
                            If there are {"{{cupStatusQualificationNumbers}}"}{" "}
                            teams in the associated league, then each team will
                            have an opponent in Gameweek{" "}
                            {"{{nextQualifyingEvent}}"}. If there are 3 teams in
                            the league, 1 team will receive a bye in Gameweek{" "}
                            {"{{nextQualifyingEvent}}"} based on their score in
                            Gameweek {"{{qualifyingEvent}}"}.
                          </Trans>
                        ) : (
                          <Trans
                            ns="leaguesAndCups"
                            i18nKey="cups.qualificationInformationRange"
                            values={{
                              cupStatusQualificationNumbers:
                                cupStatus.qualification_numbers,
                              nextQualifyingEvent:
                                cupStatus.qualification_event + 1,
                              cupStatusQualificationNumbersHalf:
                                cupStatus.qualification_numbers / 2 + 1,
                              cupStatusQualificationNumbersMinusOne:
                                cupStatus.qualification_numbers - 1,
                              qualifyingEvent: cupStatus.qualification_event,
                            }}
                          >
                            If there are {"{{cupStatusQualificationNumbers}}"}{" "}
                            teams in the associated league, then each team will
                            have an opponent in Gameweek{" "}
                            {"{{nextQualifyingEvent}}"}. If there are between{" "}
                            {"{{cupStatusQualificationNumbersHalf}}"} and{" "}
                            {"{{cupStatusQualificationNumbersMinusOne}}"} teams
                            in the league, some teams will receive a bye in
                            Gameweek {"{{nextQualifyingEvent}}"} based on their
                            score in Gameweek {"{{qualifyingEvent}}"}.
                          </Trans>
                        ))}
                    </p>

                    <p>
                      <Trans
                        ns="leaguesAndCups"
                        i18nKey="cups.startingRoundDetermination"
                      >
                        The starting round of the cup is determined by the
                        number of teams in the associated league and the final
                        will be contested in Gameweek 38.
                      </Trans>
                    </p>

                    <p>
                      <Trans ns="leaguesAndCups" i18nKey="cups.entryConditions">
                        You will not be entered into the cup if you have joined
                        the league after the Gameweek prior to the cup starting.
                      </Trans>
                    </p>

                    <Subheading>
                      {t("cups.howTheCupWorks.title", "How the cup works")}
                    </Subheading>
                    <p>
                      <Trans
                        ns="leaguesAndCups"
                        i18nKey="cups.howTheCupWorks.p1"
                      >
                        Each team will be randomly drawn against another unless
                        they have received a bye. The winner (the team with the
                        highest Gameweek score minus any transfer points), will
                        progress to the next round and another random draw, the
                        losers are out! This process continues until the final
                        round when the two remaining teams contest the cup
                        final. If a cup match is drawn, then the following
                        tie-breaks will be applied until a winner is found:
                      </Trans>
                    </p>
                    <ol>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.mostGoals",
                          "Most goals scored in the Gameweek",
                        )}
                      </li>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.fewestGoalsConceded",
                          "Fewest goals conceded in the Gameweek",
                        )}
                      </li>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.virtualCoinToss",
                          "Virtual coin toss",
                        )}
                      </li>
                    </ol>
                  </>
                ) : (
                  <>
                    <p>
                      <Trans
                        ns="leaguesAndCups"
                        i18nKey="cups.howTheCupWorks.p2"
                        values={{
                          cupStatusQualificationNumbers:
                            cupStatus.qualification_numbers,
                          qualifyingEvent: cupStatus.qualification_event,
                          nextQualifyingEvent:
                            cupStatus.qualification_event + 1,
                        }}
                      >
                        The top {"{{cupStatusQualificationNumbers}}"} ranked
                        managers in Gameweek {"{{qualifyingEvent}}"} will be
                        entered into the first round of the cup in Gameweek{" "}
                        {"{{nextQualifyingEvent}}"}. The final will be contested
                        in Gameweek 38.
                      </Trans>
                    </p>

                    <Subheading>
                      {t("cups.howTheCupWorks.title", "How the cup works")}
                    </Subheading>
                    <p>
                      <Trans
                        ns="leaguesAndCups"
                        i18nKey="cups.howTheCupWorks.p3"
                      >
                        Each qualifying team will be randomly drawn against
                        another in the first round. The winner (the team with
                        the highest Gameweek score minus any transfer points),
                        will progress to the second round and another random
                        draw, the losers are out! This process continues until
                        the final round when the two remaining teams contest the
                        cup final. If a cup match is drawn, then the following
                        tie-breaks will be applied until a winner is found:
                      </Trans>
                    </p>
                    <ol>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.mostGoals",
                          "Most goals scored in the Gameweek",
                        )}
                      </li>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.fewestGoalsConceded",
                          "Fewest goals conceded in the Gameweek",
                        )}
                      </li>
                      <li>
                        {t(
                          "cups.howTheCupWorks.tieBreaks.virtualCoinToss",
                          "Virtual coin toss",
                        )}
                      </li>
                    </ol>
                  </>
                )}
              </>
            ) : (
              <p>
                {t(
                  "cups.noInformation",
                  "There is no information available for this cup yet.",
                )}
              </p>
            )}
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default CupNotStarted;
